import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Stack from "@mui/material/Stack";
import AppBar from "@mui/material/AppBar";
import { useAuth } from "react-oidc-context";
import Toolbar from "@mui/material/Toolbar";
import Typography from '@mui/material/Typography';
import Box from "@mui/material/Box";
import { useTheme, useMediaQuery, alpha, keyframes } from "@mui/material";
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

import AppContext, { AppContextType } from "./AppContext";
import ProtectedRoute from "./ProtectedRoute";
import Callback from "./Callback";
import PublicLogout from "./PublicLogout";
import Home from "./Home";
import Admin from "./Admin";
import Login from "./Login";
import ChatPage from "./ChatPage";
import ReportPage from "./ReportPage";

// Keyframe for menu bar slide down animation
const slideDown = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

// Define a type for the user profile from Cognito
interface UserProfile {
  "cognito:groups"?: string[];
  [key: string]: any;
}

interface ChatMessage {
  text: string;
  isBot: boolean;
  chartImage?: string;
  questionCategory?: string;
  embeddedQuestion?: string;
}

function App() {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const auth = useAuth();
    const [userProfile, setUserProfile] = React.useState<UserProfile | null>(null);
    const [surveyResult, setSurveyResult] = React.useState<any[]>([]);
    
    // Breakpoint check: 'sm' is 600px. iPad Air is ~820px, so it counts as Desktop (!isSmallScreen)
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [chatHistory, setChatHistory] = useState<any[]>([]);
    const [submittedFeedback, setSubmittedFeedback] = useState<number[]>([]);
    
    // Landing mode: true = clean initial state, false = app bar visible
    // Initialize to true by default to prevent menu bar flicker during auth redirects
    const [isLandingMode, setIsLandingMode] = useState(true);
    
    // Track if menu bar animation has completed
    const [menuBarAnimationComplete, setMenuBarAnimationComplete] = useState(false);
    
    const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
    const userMenuOpen = Boolean(userMenuAnchor);
    
    const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setUserMenuAnchor(event.currentTarget);
    };
    
    const handleUserMenuClose = () => {
        setUserMenuAnchor(null);
    };

    const desktopMenuFontSize = "0.875rem";

    React.useEffect(() => {
      if (auth.isAuthenticated && auth.user?.profile) {
        const profileData = auth.user.profile as UserProfile;
        const userGroups = auth.user.profile["cognito:groups"] as string[] | undefined;

        setUserProfile({
          ...profileData,
          "cognito:groups": userGroups,
        });
      } else {
        setUserProfile(null);
      }
    }, [auth.isAuthenticated, auth.user?.profile]);

    // Handle menu bar animation state
    React.useEffect(() => {
      if (isLandingMode) {
        // Reset animation state when returning to landing mode
        setMenuBarAnimationComplete(false);
      } else {
        // Menu bar is appearing, set animation complete after 400ms (animation duration)
        const timer = setTimeout(() => {
          setMenuBarAnimationComplete(true);
        }, 400);
        return () => clearTimeout(timer);
      }
    }, [isLandingMode]);

    // --- Navigation Handlers ---
    const handleNavigateHome = () => {
      setIsLandingMode(true);
      navigate(`/`);
    };
    const handleNavigateAdmin = () => {
      const userGroups = userProfile?.["cognito:groups"] || [];
      if (auth.isAuthenticated && 
          (userGroups.includes('admin') || userGroups.includes('quantum'))
      ) {
        navigate(`/admin`);
      } else {
        navigate('/login');
      }
    };
    const handleNavigateLogin = () => navigate(`/login`);
    const handleLogout = () => navigate("/public-logout");

    const contextValue: AppContextType = {
        user: userProfile,
        setUser: setUserProfile,
        surveyResult,
        setSurveyResult,
        chatMessages,
        setChatMessages,
        chatHistory,
        setChatHistory,
        submittedFeedback,
        setSubmittedFeedback,
        isLandingMode,
        setIsLandingMode,
        menuBarAnimationComplete,
    };

    return (
        <Box sx={{ 
            display: 'flex', 
            // 100dvh is robust for mobile browsers
            height: '100dvh', 
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'background.default' 
        }}>
            <AppContext.Provider value={contextValue}>
                
                {/* 1. FIXED PROFILE ICON (Desktop/iPad) */}
                {/* We add top padding to this absolute element so it clears the status bar on iPad */}
                {!isSmallScreen && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 'calc(14px + env(safe-area-inset-top))', // FIX: Add safe area to top position
                            right: 16,
                            zIndex: theme.zIndex.drawer + 2, 
                        }}
                    >
                        {auth.isAuthenticated && userProfile ? (
                            <IconButton
                                onClick={handleUserMenuOpen}
                                size="small"
                                sx={{ 
                                    color: theme.palette.text.secondary,
                                    '&:hover': { color: theme.palette.primary.main }
                                }}
                            >
                                <AccountCircleIcon sx={{ fontSize: 28 }} />
                            </IconButton>
                        ) : (
                            <IconButton
                                onClick={handleNavigateLogin}
                                size="small"
                                sx={{ 
                                    color: theme.palette.text.secondary,
                                    '&:hover': { color: theme.palette.primary.main }
                                }}
                            >
                                <AccountCircleIcon sx={{ fontSize: 28 }} />
                            </IconButton>
                        )}
                    </Box>
                )}

                {/* 2. MENU CONTAINER */}
                {/* Slides down from top when leaving landing mode */}
                <Box sx={{
                    flexShrink: 0,
                    zIndex: theme.zIndex.drawer + 1,
                    visibility: isLandingMode ? 'hidden' : 'visible',
                    height: isLandingMode ? 0 : 'auto',
                    overflow: 'hidden',
                    bgcolor: theme.palette.background.paper,
                    pt: isLandingMode ? 0 : 'env(safe-area-inset-top)',
                    borderBottom: isLandingMode ? 0 : 1,
                    borderColor: 'divider',
                    animation: !isLandingMode ? `${slideDown} 0.4s ease-out forwards` : 'none',
                }}>
                    <AppBar 
                        position="static" 
                        color="default" 
                        elevation={0} // Remove elevation since we handle border manually or let content flow
                        sx={{ 
                            bgcolor: 'transparent', // Transparent because parent Box handles the color
                        }}
                    >
                        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
                            <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                                <Stack direction="row" sx={{ alignItems: "center", gap: { xs: 1, sm: 2 } }}>
                                    <Typography 
                                        id="navbar-title"
                                        variant="h6" 
                                        noWrap 
                                        component="a" 
                                        onClick={handleNavigateHome} 
                                        sx={{ 
                                            fontFamily: "Comic Sans MS", 
                                            fontWeight: 500, 
                                            color: "inherit", 
                                            textDecoration: "none", 
                                            display: "flex", 
                                            alignItems: "center", 
                                            cursor: 'pointer', 
                                            '&:hover': { color: 'primary.main' } 
                                        }}
                                    >
                                        <img src="/icon.png" alt="QA Bot icon" height="25em" style={{ paddingRight: "0.3em", verticalAlign: 'middle' }} /> Q Interrogator
                                    </Typography>
                                    {!isSmallScreen && (
                                        <>
                                            {auth.isAuthenticated &&
                                            userProfile && userProfile["cognito:groups"] &&
                                            (userProfile["cognito:groups"].includes('admin') || userProfile["cognito:groups"].includes('quantum')) && (
                                            <Typography variant="button" component="a" onClick={handleNavigateAdmin} sx={{ fontSize: desktopMenuFontSize, color: "inherit", textDecoration: "none", cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>Admin</Typography>
                                            )}
                                        </>
                                    )}
                                </Stack>

                                {/* Right Side Logic */}
                                {isSmallScreen ? (
                                    /* Mobile: Show Icon in AppBar */
                                    <IconButton
                                        onClick={handleUserMenuOpen}
                                        size="small"
                                        sx={{ 
                                            color: theme.palette.text.secondary,
                                            '&:hover': { color: theme.palette.primary.main }
                                        }}
                                    >
                                        <AccountCircleIcon sx={{ fontSize: 28 }} />
                                    </IconButton>
                                ) : (
                                    /* Desktop/iPad: Show Spacer (Icon is floating fixed) */
                                    <Box sx={{ width: 40 }} />
                                )}
                            </Stack>
                        </Toolbar>
                    </AppBar>
                </Box>

                {/* Shared User Menu */}
                <Menu
                    id="user-menu"
                    anchorEl={userMenuAnchor}
                    open={userMenuOpen}
                    onClose={handleUserMenuClose}
                    onClick={handleUserMenuClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    slotProps={{
                        paper: {
                            elevation: 3,
                            sx: {
                                mt: 1,
                                minWidth: 180,
                                borderRadius: 2,
                                '& .MuiMenuItem-root': { px: 2, py: 1 },
                            },
                        },
                    }}
                >
                     {auth.isAuthenticated && userProfile && [
                            <MenuItem key="user-info" disabled sx={{ opacity: '1 !important' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {userProfile.preferred_username || userProfile.username || userProfile["cognito:username"] || userProfile.email || 'User'}
                                </Typography>
                            </MenuItem>,
                            <Divider key="divider-1" />
                    ]}
                    {isSmallScreen && [
                            <MenuItem key="home" onClick={handleNavigateHome}>Home</MenuItem>,
                            auth.isAuthenticated && userProfile && userProfile["cognito:groups"] && (userProfile["cognito:groups"].includes('admin') || userProfile["cognito:groups"].includes('quantum')) && (
                                <MenuItem key="admin" onClick={handleNavigateAdmin}>Admin</MenuItem>
                            ),
                            <Divider key="divider-2" />
                    ]}
                    {auth.isAuthenticated && userProfile ? (
                        <MenuItem onClick={handleLogout}>Logout</MenuItem>
                    ) : (
                        <MenuItem onClick={handleNavigateLogin}>Login</MenuItem>
                    )}
                </Menu>

                {/* 3. MAIN CONTAINER */}
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1, 
                        bgcolor: 'background.default',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden', 
                        position: 'relative',
                    }}
                >
                    <Routes>
                       <Route path="/login" element={<Login />} />
                       <Route path="/callback" element={<Callback />} />
                       <Route path="/public-logout" element={<PublicLogout />} />
                       <Route element={<ProtectedRoute />}>
                         <Route path="/" element={<Home />} />
                         <Route path="/admin" element={<Admin />} />
                         <Route path="/prompt" element={<ChatPage />} />
                         <Route path="/sessions/:sessionId/report" element={<ReportPage />} />
                       </Route>
                    </Routes>
                </Box>
            </AppContext.Provider>
        </Box>
    );
}

export default App;